import type { Money, ProductCard } from '@repo/shopify';
import {
	getDefaultSellingPlan,
	getSellingPlanPercentOff,
	getSellingPlanUnitPrice,
	getSubscriptionPlans,
} from './selling-plans';

type ProductCardPricingInput = Pick<
	ProductCard,
	'priceRange' | 'compareAtPriceRange' | 'sellingPlanGroups'
>;

export interface ProductCardPricing {
	price: Money;
	compareAtPrice: Money | null;
	discountPercent: number | null;
	kind: 'regular' | 'sale' | 'subscription';
}

/**
 * Resolves the price treatment shown on listing cards. A discounted recurring
 * plan takes precedence over a one-time sale because Subscribe & Save is the
 * default PDP purchase option; otherwise the existing Shopify sale treatment
 * is preserved.
 */
export function getProductCardPricing(product: ProductCardPricingInput): ProductCardPricing {
	const oneTimePrice = product.priceRange.minVariantPrice;
	const subscriptionPlan = getDefaultSellingPlan(getSubscriptionPlans(product));

	if (subscriptionPlan) {
		const discountPercent = getSellingPlanPercentOff(oneTimePrice, subscriptionPlan);
		if (discountPercent !== null) {
			// Subscription cards show the plan price alone — no strikethrough
			// compare-at, no percent chip (those read as a one-time markdown).
			return {
				price: getSellingPlanUnitPrice(oneTimePrice, subscriptionPlan),
				compareAtPrice: null,
				discountPercent: null,
				kind: 'subscription',
			};
		}
	}

	const compareAtPrice = product.compareAtPriceRange?.minVariantPrice ?? null;
	const isOnSale = Boolean(
		compareAtPrice &&
			Number.parseFloat(compareAtPrice.amount) > Number.parseFloat(oneTimePrice.amount),
	);

	return {
		price: oneTimePrice,
		compareAtPrice: isOnSale ? compareAtPrice : null,
		discountPercent: null,
		kind: isOnSale ? 'sale' : 'regular',
	};
}
